import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatsCard } from "@/components/stats-card";
import {
  Package, Building2, Globe, DollarSign, RefreshCcw, Loader2,
  ChevronDown, ChevronUp, AlertTriangle, BarChart3, Search, ArrowUpDown,
  FileText, Code2, Heading1, Image, Download, FileJson, Filter, X,
  Activity, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { alibabaApi, type InsightsReport as IReport, type PipelineStatus, type AiSummaryRecord } from "@/api/alibaba";

// ── Color palette ──

const CHART_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#f97316", "#84cc16", "#ec4899", "#14b8a6",
];

// ── Page component ──

export default function InsightReport() {
  const { tag } = useParams<{ tag: string }>();
  const decodedTag = tag ? decodeURIComponent(tag) : "";
  const queryClient = useQueryClient();

  const { data: report, isLoading, error } = useQuery({
    queryKey: ["alibaba-insights", decodedTag],
    queryFn: async () => {
      const res = await alibabaApi.getInsights(decodedTag);
      return res.data as IReport;
    },
    enabled: !!decodedTag,
  });

  // Pipeline status query — gracefully handles 404 if Task B API not deployed yet
  const { data: pipeline } = useQuery({
    queryKey: ["alibaba-pipeline", decodedTag],
    queryFn: async () => {
      try {
        const res = await alibabaApi.getPipelineStatus(decodedTag);
        return res.data as PipelineStatus;
      } catch {
        return null; // API not ready yet
      }
    },
    enabled: !!decodedTag,
    retry: false,
  });

  // AI summaries from DB (pre-loaded, includes stale detection)
  const { data: aiSummariesData, refetch: refetchAiSummaries } = useQuery({
    queryKey: ["alibaba-ai-summaries", decodedTag],
    queryFn: async () => {
      try {
        const res = await alibabaApi.getAiSummaries(decodedTag);
        const list = res.data as AiSummaryRecord[];
        return Object.fromEntries(list.map(r => [r.attr_key, r]));
      } catch {
        return {} as Record<string, AiSummaryRecord>;
      }
    },
    enabled: !!decodedTag,
    retry: false,
  });
  const dbAiSummaries = aiSummariesData ?? {};

  // Suppliers competitive analysis
  const { data: suppliersAnalysisData } = useQuery({
    queryKey: ["suppliers-analysis", decodedTag],
    queryFn: async () => {
      try {
        const res = await alibabaApi.getSuppliersAnalysis(decodedTag);
        return res.data;
      } catch {
        return null;
      }
    },
    enabled: !!decodedTag,
    retry: false,
  });
  const suppliersAnalysis = suppliersAnalysisData ?? null;

  const parseMutation = useMutation({
    mutationFn: () => alibabaApi.parseProducts(decodedTag),
    onSuccess: (res) => {
      toast.success(`Parsed ${res.data?.parsed ?? 0} products, ${res.data?.failed ?? 0} failed`);
      queryClient.invalidateQueries({ queryKey: ["alibaba-insights", decodedTag] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const triggerRecrawlMutation = useMutation({
    mutationFn: () => alibabaApi.triggerRecrawl(decodedTag),
    onSuccess: (res) => {
      const msg = res.data?.message || 'Recrawl started';
      toast.success(msg);
      queryClient.invalidateQueries({ queryKey: ["alibaba-pipeline", decodedTag] });
    },
    onError: (err: Error) => toast.error(`Recrawl failed: ${err.message}`),
  });

  if (!decodedTag) {
    return <div className="p-6 text-muted-foreground">No tag specified</div>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading insights for "{decodedTag}"...</span>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6">
        <div className="text-destructive flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          <span>Failed to load: {(error as Error)?.message || "Unknown error"}</span>
        </div>
        <Button variant="outline" className="mt-4" onClick={() => parseMutation.mutate()}>
          <RefreshCcw className="h-4 w-4 mr-2" />
          Parse Products First
        </Button>
      </div>
    );
  }

  const { overview, price_analysis, attribute_coverage, raw_attribute_coverage, supplier_distribution, seo_coverage, competitor_list } = report;
  // 新增：
  const co = report.co_insights
  const iframe = report.iframe_insights

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      {/* Pipeline status bar */}
      {pipeline && pipeline.total > 0 && (
        <PipelineBar
          data={pipeline}
          onRecrawl={() => triggerRecrawlMutation.mutate()}
          isRecrawling={triggerRecrawlMutation.isPending}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{decodedTag}</h1>
          <p className="text-sm text-muted-foreground">
            Product Insights — {overview.total_products} products / {overview.total_companies} companies
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportCSV(competitor_list, decodedTag)}>
            <Download className="h-4 w-4 mr-1" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportJSON(report, decodedTag)}>
            <FileJson className="h-4 w-4 mr-1" />JSON
          </Button>
          <Button variant="outline" size="sm" onClick={() => alibabaApi.downloadExcel(decodedTag)}>
            <Download className="h-4 w-4 mr-1" />Excel
          </Button>
          <Button variant="outline" onClick={() => parseMutation.mutate()} disabled={parseMutation.isPending}>
            {parseMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
            Re-analyze
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">產品分析</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers 競爭</TabsTrigger>
          <TabsTrigger value="co">CO 公司</TabsTrigger>
          <TabsTrigger value="iframe">商品介紹</TabsTrigger>
          <TabsTrigger value="analysis">📊 分析報告</TabsTrigger>
        </TabsList>

        {/* Products tab */}
        <TabsContent value="products" className="space-y-6 mt-4">
          {/* Overview cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatsCard title="Products 產品數" value={overview.total_products} icon={Package} color="info" />
            <StatsCard title="Companies 廠商數" value={overview.total_companies} icon={Building2} color="success" />
            <StatsCard title="Countries 國家" value={overview.countries} icon={Globe} color="warning" />
            <StatsCard title="Avg Price 均價" value={`$${overview.avg_price.toFixed(2)}`} icon={DollarSign} color="primary" />
          </div>

          {/* Price + Supplier */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PriceCard data={price_analysis} />
            <SupplierCard data={supplier_distribution} />
          </div>

          {/* Attribute coverage */}
          {attribute_coverage.length > 0 && (
            <AttributeCard
              attributes={attribute_coverage}
              rawAttributes={raw_attribute_coverage ?? []}
              total={overview.total_products}
              tag={decodedTag}
              dbAiSummaries={dbAiSummaries}
              onAiSummaryUpdate={refetchAiSummaries}
            />
          )}

          {/* SEO coverage */}
          <SeoCard data={seo_coverage} />

          {/* Competitor list */}
          {competitor_list.length > 0 && <CompetitorCard items={competitor_list} />}
        </TabsContent>

        {/* Suppliers competitive analysis tab */}
        <TabsContent value="suppliers" className="mt-4">
          {suppliersAnalysis ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Suppliers 競爭分析
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary row */}
                <div className="flex gap-6 text-sm">
                  <span>🏢 <strong>{suppliersAnalysis.summary?.total ?? 0}</strong> 家供應商</span>
                  <span>🥇 <strong>{suppliersAnalysis.summary?.gold_count ?? 0}</strong> Gold</span>
                  <span>🏭 <strong>{suppliersAnalysis.summary?.factory_count ?? 0}</strong> 工廠</span>
                </div>

                {/* Country distribution */}
                {suppliersAnalysis.country_distribution?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-2">國家分布 Top 10</div>
                    {suppliersAnalysis.country_distribution.map((row) => (
                      <div key={row.country_code} className="flex justify-between text-xs py-1 border-b">
                        <span>{row.country_code}</span>
                        <span>{row.count} 家{row.avg_rating != null ? ` | ⭐${row.avg_rating}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Top rated suppliers */}
                {suppliersAnalysis.top_suppliers?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-2">評分 Top 10</div>
                    {suppliersAnalysis.top_suppliers.map((s) => (
                      <div key={s.company_id} className="flex justify-between text-xs py-1 border-b">
                        <span className="truncate max-w-48">{s.company_name}</span>
                        <span>
                          {s.rating_score != null ? `⭐${s.rating_score}` : '—'}
                          {s.reviews_count != null ? ` (${s.reviews_count}則)` : ''}
                          {s.gold_supplier ? ' 🥇' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {suppliersAnalysis.country_distribution?.length === 0 && suppliersAnalysis.top_suppliers?.length === 0 && (
                  <p className="text-sm text-muted-foreground">此關鍵字尚無 Suppliers 競爭資料，請先執行 SC 階段抓取供應商資料。</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              載入供應商競爭資料...
            </div>
          )}
        </TabsContent>

        {/* CO 公司 tab */}
        <TabsContent value="co" className="mt-4 space-y-4">
          {!co ? (
            <div className="text-sm text-muted-foreground p-4">CO 資料未載入（後端尚未部署 Tasks_11-A）</div>
          ) : (
            <>
              {/* 覆蓋率卡片 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatsCard title="CO 總數" value={co.co_total} icon={Building2} color="info" />
                <StatsCard title="QS 完成" value={`${co.co_qs_done} / ${co.co_total}`} icon={Activity} color="success" />
                <StatsCard title="BA 廠區完成" value={co.co_ba_done} icon={Activity} color="warning" />
                <StatsCard title="有廠區照片" value={co.co_has_factory} icon={Image} color="primary" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatsCard title="Gold 供應商" value={co.co_gold_count} icon={Building2} color="warning" />
                <StatsCard title="工廠型" value={co.co_factory_count} icon={Building2} color="info" />
                <StatsCard title="Trade Assurance" value={co.co_trade_assurance} icon={Activity} color="success" />
                <StatsCard title="平均交易數" value={co.avg_transactions != null ? Math.round(co.avg_transactions) : '—'} icon={BarChart3} color="primary" />
              </div>

              {/* 認證清單 */}
              {co.top_certifications && co.top_certifications.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">認證清單 Top</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {co.top_certifications.slice(0, 30).map((cert) => (
                        <Badge key={cert} variant="outline" className="text-xs">{cert}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* 國家分布 */}
              {co.country_breakdown && co.country_breakdown.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">國家分布</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={co.country_breakdown.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="country" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* 商品介紹 tab */}
        <TabsContent value="iframe" className="mt-4 space-y-4">
          {!iframe ? (
            <div className="text-sm text-muted-foreground p-4">iframe 資料未載入（後端尚未部署 Tasks_11-A）</div>
          ) : (
            <>
              {/* 覆蓋率統計 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatsCard title="總商品數" value={iframe.pd_total} icon={Package} color="info" />
                <StatsCard
                  title="有截圖"
                  value={`${iframe.has_screenshot} (${iframe.pd_total ? Math.round(iframe.has_screenshot * 100 / iframe.pd_total) : 0}%)`}
                  icon={Image}
                  color="success"
                />
                <StatsCard
                  title="有 Markdown"
                  value={`${iframe.has_markdown} (${iframe.pd_total ? Math.round(iframe.has_markdown * 100 / iframe.pd_total) : 0}%)`}
                  icon={FileText}
                  color="primary"
                />
                <StatsCard title="CAPTCHA 封鎖" value={iframe.captcha_blocked} icon={AlertTriangle} color="warning" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatsCard title="有 iframe 圖片" value={iframe.has_iframe_images} icon={Image} color="info" />
                <StatsCard title="平均圖片數" value={iframe.avg_iframe_images != null ? iframe.avg_iframe_images : '—'} icon={BarChart3} color="primary" />
              </div>

              {/* 截圖 Gallery */}
              {iframe.top_products_with_media.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">商品介紹截圖 Top 12（依圖片數排序）</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {iframe.top_products_with_media.map((p) => (
                        <div key={p.task_id} className="border rounded-lg overflow-hidden">
                          <img
                            src={p.screenshot_url}
                            alt={p.product_name ?? 'product'}
                            className="w-full h-32 object-cover object-top"
                            loading="lazy"
                          />
                          <div className="p-2 text-xs space-y-0.5">
                            <div className="font-medium truncate">{p.product_name ?? '—'}</div>
                            <div className="text-muted-foreground">{p.company_name ?? '—'}</div>
                            {p.price_min != null && (
                              <div className="text-green-700">${p.price_min}</div>
                            )}
                            <Badge variant="outline" className="text-xs">{p.iframe_image_count} 張圖</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Markdown 預覽列表 */}
              {iframe.top_products_with_media.filter(p => p.markdown_preview).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">商品 Markdown 預覽</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {iframe.top_products_with_media
                      .filter(p => p.markdown_preview)
                      .slice(0, 6)
                      .map((p) => (
                        <div key={p.task_id} className="border rounded p-3 text-xs">
                          <div className="font-medium mb-1">{p.product_name ?? '—'}</div>
                          <div className="text-muted-foreground whitespace-pre-wrap line-clamp-4">
                            {p.markdown_preview}
                          </div>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* 分析報告 tab */}
        <TabsContent value="analysis" className="space-y-6 mt-4">
          <IndustryAnalysisCard tag={decodedTag} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Export helpers ──

function exportCSV(items: IReport["competitor_list"], tag: string) {
  const header = "Company,Country,Years,Trade Assurance,Products,Price Range";
  const rows = items.map(c =>
    [
      `"${c.company_name.replace(/"/g, '""')}"`,
      c.country || "",
      c.years ?? "",
      c.has_trade_assurance ? "Yes" : "No",
      c.products,
      `"${c.price_range}"`,
    ].join(",")
  );
  const csv = [header, ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${tag.replace(/\s+/g, "_")}_competitors.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportJSON(report: IReport, tag: string) {
  const json = JSON.stringify(report, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${tag.replace(/\s+/g, "_")}_insights.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Pipeline Status Bar ──

function PipelineBar({ data, onRecrawl, isRecrawling }: { data: PipelineStatus; onRecrawl?: () => void; isRecrawling?: boolean }) {
  const segments = [
    { label: "Scraped", count: data.scraped, color: "bg-blue-500" },
    { label: "Parsed", count: data.parsed, color: "bg-green-500" },
    { label: "Failed", count: data.failed, color: "bg-red-500" },
    { label: "Re-crawl", count: data.needs_recrawl, color: "bg-yellow-500" },
  ];
  const total = data.total || 1;

  return (
    <Card>
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-3 mb-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Pipeline Status 管線進度</span>
          <span className="text-xs text-muted-foreground ml-auto">Total: {data.total}</span>
          {data.needs_recrawl > 0 && onRecrawl && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRecrawl}
              disabled={isRecrawling}
              className="h-7 text-xs"
            >
              {isRecrawling ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              批次重爬 ({data.needs_recrawl})
            </Button>
          )}
        </div>
        {/* Progress bar */}
        <div className="h-2.5 rounded-full bg-muted overflow-hidden flex">
          {segments.map(seg => seg.count > 0 && (
            <div
              key={seg.label}
              className={`${seg.color} transition-all`}
              style={{ width: `${(seg.count / total) * 100}%` }}
              title={`${seg.label}: ${seg.count}`}
            />
          ))}
        </div>
        {/* Legend */}
        <div className="flex gap-4 mt-1.5">
          {segments.map(seg => (
            <span key={seg.label} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <span className={`inline-block w-2 h-2 rounded-full ${seg.color}`} />
              {seg.label} {seg.count}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Price Card ──

function PriceCard({ data }: { data: IReport["price_analysis"] }) {
  if (data.histogram.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" />Price Distribution 價格分布</CardTitle></CardHeader>
        <CardContent><p className="text-muted-foreground text-sm">No price data available</p></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" />Price Distribution 價格分布</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 mb-4 text-center text-sm">
          <div><span className="text-muted-foreground block">Min</span><span className="font-bold">${data.min.toFixed(2)}</span></div>
          <div><span className="text-muted-foreground block">Avg</span><span className="font-bold">${data.avg.toFixed(2)}</span></div>
          <div><span className="text-muted-foreground block">Max</span><span className="font-bold">${data.max.toFixed(2)}</span></div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.histogram}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="range" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
            <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ── Supplier Card ──

function SupplierCard({ data }: { data: IReport["supplier_distribution"] }) {
  const countryData = data.by_country.map((c, i) => ({
    name: c.country || "N/A",
    value: c.count,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" />Supplier Distribution 廠商分布</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1 text-center">By Country 國家</p>
            {countryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={countryData}
                    dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={70}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {countryData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center pt-8">No data</p>}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1 text-center">By Years 年資</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.by_years}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Attribute Coverage Card ──

// Tokenize a raw attribute value string into individual tokens
function tokenizeValue(value: string): string[] {
  return value
    .split(/[\/，、,；;+]|\s+or\s+|\s+OR\s+/)
    .map(t => t.trim())
    .filter(t => t.length > 0 && t.length < 60);
}

// Build normalized (token-level) values from raw top_values
function buildNormalizedValues(rawValues: Array<{ value: string; count: number }>) {
  const tokenMap = new Map<string, number>();
  for (const { value, count } of rawValues) {
    const tokens = tokenizeValue(value);
    for (const token of tokens) {
      tokenMap.set(token, (tokenMap.get(token) ?? 0) + count);
    }
  }
  return Array.from(tokenMap.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
}

type AttrViewMode = "raw" | "normalized" | "ai";

function AttributeCard({ attributes, rawAttributes, total, tag, dbAiSummaries = {}, onAiSummaryUpdate }: {
  attributes: IReport["attribute_coverage"];
  rawAttributes: IReport["attribute_coverage"];
  total: number;
  tag: string;
  dbAiSummaries?: Record<string, AiSummaryRecord>;
  onAiSummaryUpdate?: () => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<AttrViewMode>("raw");
  // Local overrides (after user runs single AI on current session)
  const [aiSummaries, setAiSummaries] = useState<Record<string, Array<{ value: string; count: number }>>>({});
  const [aiLoading, setAiLoading] = useState<Set<string>>(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; attrKey: string } | null>(null);

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const runAiSummary = async (attrKey: string, rawValues: Array<{ value: string; count: number }>) => {
    setAiLoading(prev => new Set(prev).add(attrKey));
    try {
      const res = await alibabaApi.normalizeAttrValues(tag, attrKey, rawValues);
      const result = res.data as Array<{ value: string; count: number }>;
      setAiSummaries(prev => ({ ...prev, [attrKey]: result ?? [] }));
      onAiSummaryUpdate?.();
    } catch (e) {
      console.error("AI normalize failed:", e);
    } finally {
      setAiLoading(prev => { const n = new Set(prev); n.delete(attrKey); return n; });
    }
  };

  const runBatchAi = async (onlyStale: boolean) => {
    setBatchRunning(true);
    setBatchProgress({ current: 0, total: 0, attrKey: "" });
    try {
      const res = await alibabaApi.batchAiSummaries(tag, onlyStale);
      const result = res.data as { done: boolean; processed: number; failed: number; total: number };
      toast.success(`批次彙整完成：${result.processed}/${result.total} 屬性成功${result.failed > 0 ? `，${result.failed} 失敗` : ""}`);
      onAiSummaryUpdate?.();
    } catch (e) {
      toast.error("批次彙整失敗");
      console.error(e);
    } finally {
      setBatchRunning(false);
      setBatchProgress(null);
    }
  };

  // Which attribute list to render based on view mode
  // Raw mode → original attr_key list (pre-normalization, many keys)
  // Normalized/AI → standard_key list (~14 keys)
  const displayAttributes = viewMode === "raw" ? (rawAttributes.length > 0 ? rawAttributes : attributes) : attributes;

  // Resolved values per attribute
  const getValues = (attr: IReport["attribute_coverage"][number]) => {
    if (viewMode === "raw") return attr.values;
    if (viewMode === "normalized") return buildNormalizedValues(attr.values);
    if (viewMode === "ai") {
      if (aiSummaries[attr.attr_key]) return aiSummaries[attr.attr_key];
      if (dbAiSummaries[attr.attr_key]) return dbAiSummaries[attr.attr_key].summarized_values;
      return buildNormalizedValues(attr.values);
    }
    return attr.values;
  };

  const staleCount = Object.values(dbAiSummaries).filter(r => r.stale).length;
  const totalSaved = Object.keys(dbAiSummaries).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Attribute Coverage 屬性覆蓋率</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Batch AI button — visible when in AI mode */}
            {viewMode === "ai" && (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={batchRunning}
                  onClick={() => runBatchAi(false)}
                  className="text-xs h-7"
                >
                  {batchRunning
                    ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />彙整中 {batchProgress?.current}/{batchProgress?.total}</>
                    : <><Activity className="h-3 w-3 mr-1" />批次彙整全部 ({displayAttributes.length})</>}
                </Button>
                {staleCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={batchRunning}
                    onClick={() => runBatchAi(true)}
                    className="text-xs h-7 border-amber-400 text-amber-700 hover:bg-amber-50"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />重跑 stale ({staleCount})
                  </Button>
                )}
              </div>
            )}
            {/* XLSX download button */}
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7"
              onClick={() => window.open(`/api/alibaba/insights/${encodeURIComponent(tag)}/excel`, '_blank')}
            >
              <Download className="h-3 w-3 mr-1" />XLSX
            </Button>
            {/* Tab switcher */}
            <div className="flex items-center gap-1 bg-muted rounded-md p-1">
              <button
                onClick={() => setViewMode("raw")}
                className={`px-3 py-1 text-xs rounded transition-colors ${viewMode === "raw" ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"}`}
              >
                原始 Raw
              </button>
              <button
                onClick={() => setViewMode("normalized")}
                className={`px-3 py-1 text-xs rounded transition-colors ${viewMode === "normalized" ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"}`}
              >
                🔀 標準化
              </button>
              <button
                onClick={() => setViewMode("ai")}
                className={`px-3 py-1 text-xs rounded transition-colors ${viewMode === "ai" ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"}`}
              >
                🤖 AI 彙整
                {totalSaved > 0 && (
                  <span className="ml-1 bg-green-100 text-green-700 rounded-full px-1.5 text-[10px]">{totalSaved}</span>
                )}
              </button>
            </div>
          </div>
        </div>
        {viewMode === "normalized" && (
          <p className="text-xs text-muted-foreground mt-1">
            複合值已拆分（如「白色/蓝色」→ 白色、蓝色各計）。計數為加總，非去重產品數。
          </p>
        )}
        {viewMode === "ai" && (
          <p className="text-xs text-muted-foreground mt-1">
            {totalSaved > 0
              ? <>已存 <strong>{totalSaved}</strong> 個屬性的 AI 彙整結果。{staleCount > 0 && <span className="text-amber-600">⚠️ {staleCount} 個有新資料，建議重新彙整。</span>}</>
              : "點擊每個屬性的「AI 彙整」按鈕，或使用「批次彙整全部」一次處理所有屬性。"}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-1">
        {displayAttributes.map(attr => {
          const isOpen = expanded.has(attr.attr_key);
          const pct = total > 0 ? Math.round(attr.count / total * 100) : 0;
          const displayValues = getValues(attr);
          const isAiLocalDone = !!aiSummaries[attr.attr_key];
          const isAiDbDone = !!dbAiSummaries[attr.attr_key];
          const isAiDone = isAiLocalDone || isAiDbDone;
          const isAiPending = aiLoading.has(attr.attr_key);
          const isStale = isAiDbDone && dbAiSummaries[attr.attr_key].stale && !isAiLocalDone;
          return (
            <div key={attr.attr_key} className="border rounded-md">
              <button
                onClick={() => toggle(attr.attr_key)}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent/50 transition-colors text-left"
              >
                {isOpen ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                <span className="font-medium text-sm min-w-[120px]">{attr.attr_key}</span>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {attr.count}/{total} ({pct}%)
                </span>
                {viewMode === "ai" && (
                  <>
                    <span
                      role="button"
                      onClick={e => {
                        e.stopPropagation();
                        if (!isAiPending) runAiSummary(attr.attr_key, attr.values);
                      }}
                      className={`ml-2 text-[10px] px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap ${
                        isAiPending ? "bg-yellow-100 text-yellow-700"
                        : isStale    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        : isAiDone   ? "bg-green-100 text-green-700 hover:bg-green-200"
                        :              "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                    >
                      {isAiPending ? "⏳ 分析中..."
                        : isStale  ? "⚠️ 重新彙整"
                        : isAiDone ? "✅ 完成"
                        :            "🤖 AI 彙整"}
                    </span>
                    {isStale && (
                      <span className="text-[10px] text-amber-600 whitespace-nowrap">
                        +{(dbAiSummaries[attr.attr_key].current_product_count - dbAiSummaries[attr.attr_key].source_product_count)} 新資料
                      </span>
                    )}
                  </>
                )}
                <div className="flex-1 flex flex-wrap gap-1 ml-2">
                  {displayValues.slice(0, 5).map(v => (
                    <Badge key={v.value} variant="secondary" className="text-xs">
                      {v.value.length > 20 ? v.value.slice(0, 20) + "..." : v.value}
                      <span className="ml-1 text-[10px] bg-primary/10 rounded-full px-1">{v.count}</span>
                    </Badge>
                  ))}
                  {displayValues.length > 5 && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">+{displayValues.length - 5}</Badge>
                  )}
                </div>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 border-t bg-muted/30">
                  <div className="flex flex-wrap gap-2">
                    {displayValues.map(v => (
                      <span key={v.value} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-xs">
                        {v.value.length > 40 ? v.value.slice(0, 40) + "..." : v.value}
                        <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">
                          {v.count}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ── SEO Coverage Card ──

function SeoCard({ data }: { data: IReport["seo_coverage"] }) {
  const items = [
    { label: "Title", icon: FileText, ...data.title },
    { label: "Meta Description", icon: FileText, ...data.meta_desc },
    { label: "H1", icon: Heading1, ...data.h1 },
    { label: "Schema (Product)", icon: Code2, ...data.schema },
  ];

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">SEO Coverage SEO 覆蓋率</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map(item => {
            const pct = item.total > 0 ? Math.round(item.count / item.total * 100) : 0;
            const color = pct >= 90 ? "text-green-500" : pct >= 50 ? "text-yellow-500" : "text-red-500";
            return (
              <div key={item.label} className="text-center p-3 rounded-md border">
                <item.icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
                <div className={`text-xl font-bold ${color}`}>{pct}%</div>
                <div className="text-xs text-muted-foreground">{item.label}</div>
                <div className="text-[10px] text-muted-foreground">{item.count}/{item.total}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Competitor List Card ──

function CompetitorCard({ items }: { items: IReport["competitor_list"] }) {
  const [sortField, setSortField] = useState<string>("products");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [searchText, setSearchText] = useState("");
  const [countryFilter, setCountryFilter] = useState<Set<string>>(new Set());
  const [taFilter, setTaFilter] = useState<"all" | "yes" | "no">("all");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Extract unique countries for filter
  const countries = useMemo(() => {
    const set = new Set<string>();
    items.forEach(c => { if (c.country) set.add(c.country); });
    return Array.from(set).sort();
  }, [items]);

  const toggleCountry = (country: string) => {
    setCountryFilter(prev => {
      const next = new Set(prev);
      next.has(country) ? next.delete(country) : next.add(country);
      return next;
    });
  };

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = useMemo(() => {
    let filtered = items;
    // Text search
    if (searchText) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(c => c.company_name.toLowerCase().includes(q));
    }
    // Country filter
    if (countryFilter.size > 0) {
      filtered = filtered.filter(c => c.country != null && countryFilter.has(c.country));
    }
    // TA filter
    if (taFilter === "yes") {
      filtered = filtered.filter(c => c.has_trade_assurance);
    } else if (taFilter === "no") {
      filtered = filtered.filter(c => !c.has_trade_assurance);
    }
    return [...filtered].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sortField];
      const bv = (b as Record<string, unknown>)[sortField];
      const numA = typeof av === "number" ? av : typeof av === "string" ? av : 0;
      const numB = typeof bv === "number" ? bv : typeof bv === "string" ? bv : 0;
      if (typeof numA === "number" && typeof numB === "number") {
        return sortDir === "asc" ? numA - numB : numB - numA;
      }
      return sortDir === "asc"
        ? String(numA).localeCompare(String(numB))
        : String(numB).localeCompare(String(numA));
    });
  }, [items, searchText, countryFilter, taFilter, sortField, sortDir]);

  const hasActiveFilters = countryFilter.size > 0 || taFilter !== "all";

  const SortHeader = ({ field, label }: { field: string; label: string }) => (
    <TableHead className="cursor-pointer hover:text-foreground select-none whitespace-nowrap" onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </TableHead>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">
            Competitor List 競爭廠商 ({sorted.length}{sorted.length !== items.length ? `/${items.length}` : ""})
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="pl-7 h-9 w-[180px] text-sm"
              />
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 flex-wrap mt-2">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Filter className="h-3 w-3" /> Filters:
          </span>

          {/* Country multi-select as inline badges */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-muted-foreground">Country:</span>
            {countries.slice(0, 10).map(c => (
              <Badge
                key={c}
                variant={countryFilter.has(c) ? "default" : "outline"}
                className="text-[11px] cursor-pointer hover:opacity-80"
                onClick={() => toggleCountry(c)}
              >
                {c}
              </Badge>
            ))}
            {countries.length > 10 && (
              <span className="text-[11px] text-muted-foreground">+{countries.length - 10}</span>
            )}
          </div>

          {/* TA toggle */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">TA:</span>
            {(["all", "yes", "no"] as const).map(v => (
              <Badge
                key={v}
                variant={taFilter === v ? "default" : "outline"}
                className="text-[11px] cursor-pointer hover:opacity-80"
                onClick={() => setTaFilter(v)}
              >
                {v === "all" ? "All" : v === "yes" ? "Yes" : "No"}
              </Badge>
            ))}
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost" size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => { setCountryFilter(new Set()); setTaFilter("all"); }}
            >
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <SortHeader field="company_name" label="Company 廠商" />
                <SortHeader field="country" label="Country" />
                <SortHeader field="years" label="Years" />
                <TableHead>TA</TableHead>
                <SortHeader field="products" label="Products" />
                <SortHeader field="price_range" label="Price Range" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.slice(0, 50).map(item => (
                <CompetitorRow
                  key={item.company_name}
                  item={item}
                  isExpanded={expandedRow === item.company_name}
                  onToggle={() => setExpandedRow(prev => prev === item.company_name ? null : item.company_name)}
                />
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">No results</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {sorted.length > 50 && (
          <p className="text-xs text-muted-foreground text-center mt-2">Showing first 50 of {sorted.length}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Competitor Row (expandable) ──

function CompetitorRow({
  item,
  isExpanded,
  onToggle,
}: {
  item: IReport["competitor_list"][number];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell className="w-8 px-2">
          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </TableCell>
        <TableCell className="font-medium text-sm max-w-[250px] truncate">{item.company_name}</TableCell>
        <TableCell>{item.country || "\u2014"}</TableCell>
        <TableCell>{item.years != null ? `${item.years}yr` : "\u2014"}</TableCell>
        <TableCell>{item.has_trade_assurance ? "\u2705" : "\u2014"}</TableCell>
        <TableCell><Badge variant="secondary" className="text-xs">{item.products}</Badge></TableCell>
        <TableCell className="text-sm whitespace-nowrap">{item.price_range}</TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/30 p-0">
            <div className="px-6 py-3 text-sm space-y-2">
              {/* CO company profile data */}
              {(item.year_established || item.annual_revenue || item.rating || item.certifications_json?.length) ? (
                <div className="flex flex-wrap gap-3 text-xs">
                  {item.year_established && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      📅 Est. <span className="text-foreground font-medium">{item.year_established}</span>
                    </span>
                  )}
                  {item.annual_revenue && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      💰 <span className="text-foreground font-medium">{item.annual_revenue}</span>
                    </span>
                  )}
                  {item.rating != null && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      ⭐ <span className="text-foreground font-medium">{item.rating.toFixed(1)}</span>
                      {item.review_count != null && <span>({item.review_count} reviews)</span>}
                    </span>
                  )}
                  {item.certifications_json && item.certifications_json.length > 0 && (
                    <span className="flex items-center gap-1 text-blue-600">
                      🏆 {item.certifications_json.slice(0, 3).map(c => c.certificateName).filter(Boolean).join(' · ')}
                      {item.certifications_json.length > 3 && <span className="text-muted-foreground">+{item.certifications_json.length - 3}</span>}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No CO profile data yet — run company crawl (CO stage) to populate.
                </p>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Industry Analysis Card (LLM Executive Summary for Management) ──

interface IndustryAnalysis {
  spec_key_points: string[]
  competitor_key_points: string[]
  executive_summary: string[]
}

function IndustryAnalysisCard({ tag }: { tag: string }) {
  const [analysis, setAnalysis] = useState<IndustryAnalysis | null>(null)

  const generateMutation = useMutation({
    mutationFn: () => alibabaApi.generateIndustryAnalysis(tag),
    onSuccess: (res) => {
      const data = res.data as IndustryAnalysis
      setAnalysis(data)
      toast.success('分析完成')
    },
    onError: (err: Error) => toast.error(`分析失敗: ${err.message}`)
  })

  const copyAll = () => {
    if (!analysis) return
    const lines = [
      '【規格屬性要點】',
      ...analysis.spec_key_points,
      '',
      '【競品要點】',
      ...analysis.competitor_key_points,
      '',
      '【管理層摘要】',
      ...(Array.isArray(analysis.executive_summary) ? analysis.executive_summary : [analysis.executive_summary]),
    ]
    navigator.clipboard.writeText(lines.join('\n'))
    toast.success('已複製到剪貼簿')
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            產業分析報告 Industry Analysis
          </CardTitle>
          <div className="flex items-center gap-2">
            {analysis && (
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={copyAll}>
                複製全文
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="h-7 text-xs"
            >
              {generateMutation.isPending
                ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />分析中...</>
                : <><RefreshCw className="h-3 w-3 mr-1" />生成分析</>}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          LLM 自動分析屬性覆蓋率與競品格局，輸出濃縮管理摘要（適合主管高度閱覽）
        </p>
      </CardHeader>
      <CardContent>
        {!analysis && !generateMutation.isPending && (
          <div className="text-sm text-muted-foreground text-center py-8">
            點擊「生成分析」以產生產業洞察報告
          </div>
        )}
        {generateMutation.isPending && (
          <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>LLM 分析中，約需 15-30 秒...</span>
          </div>
        )}
        {analysis && (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <span className="text-blue-600">📐</span> 規格屬性要點 Specification Key Points
              </h3>
              <ul className="space-y-1.5">
                {analysis.spec_key_points.map((pt, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-blue-500 shrink-0">•</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <span className="text-orange-600">🏢</span> 競品要點 Competitor Key Points
              </h3>
              <ul className="space-y-1.5">
                {analysis.competitor_key_points.map((pt, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-orange-500 shrink-0">•</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-primary/5 rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <span>📊</span> 管理層摘要（給主管高度看）Executive Summary
              </h3>
              <ul className="space-y-1.5">
                {(Array.isArray(analysis.executive_summary)
                  ? analysis.executive_summary
                  : [analysis.executive_summary]
                ).map((pt, i) => (
                  <li key={i} className="text-sm font-medium flex gap-2">
                    <span className="text-primary shrink-0">▸</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
